import each from 'lodash/each';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import { api } from 'redux/api/index.api';
import { getCategoryTree } from 'utils/formCategories.utils';
import { getNameFromContentDisposition } from 'utils/request';

export const getCategoriesPathInfoRequestBody = (categories_and_forms, moduleId) => {
  const categoriesAndFormsForModule = find(
    categories_and_forms,
    categoriesAndForms => categoriesAndForms.module_id === moduleId,
  );

  const categoryIdsSet = new Set();
  const formIdsSet = new Set();

  each(categoriesAndFormsForModule?.categories_and_forms_paths, categorieAndForm => {
    formIdsSet.add(categorieAndForm.form_id);

    if (!isEmpty(categorieAndForm.category_path)) {
      const categoryIds = categorieAndForm.category_path.split('/');
      categoryIds.forEach(categoryId => categoryIdsSet.add(categoryId));
    }
  });

  return {
    categories_path_infos: [
      {
        module_id: moduleId,
        category_ids: Array.from(categoryIdsSet),
        form_ids: Array.from(formIdsSet),
      },
    ],
  };
};

export const formsApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchProjectFormsModulesFilters: builder.query({
      query: ({ projectId, searchTerm = '', limit = 1000, offset = 0 }) => ({
        url: `projects/${projectId}/forms-modules/filters`,
        method: 'POST',
        params: {
          limit,
          offset,
        },
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          search_term: searchTerm,
          visibility: null,
          creator_ids: null,
          with_disabled: true,
        },
      }),
      providesTags: ['Modules'],
      keepUnusedDataFor: 0,
    }),
    fetchCategoryTree: builder.query({
      async queryFn(arg, queryApi, extraOptions, fetchWithBq) {
        const { projectId, module } = arg;

        const categoriesAndFormsPathResult = await fetchWithBq({
          url: `projects/${projectId}/form-instances/categories-and-forms-paths`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            module_ids: [module.id],
          },
        });

        if (categoriesAndFormsPathResult.error) {
          return { error: categoriesAndFormsPathResult.error };
        }

        const { categories_and_forms_paths } = categoriesAndFormsPathResult.data;

        const pathInfosResult = await fetchWithBq({
          url: `projects/${projectId}/forms-modules/categories/path-infos`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: getCategoriesPathInfoRequestBody(categories_and_forms_paths, module.id),
        });

        if (pathInfosResult.error) {
          return { error: pathInfosResult.error };
        }

        const { data: pathInfosResponse } = pathInfosResult;

        return {
          data: {
            children: getCategoryTree(pathInfosResponse, categories_and_forms_paths),
            name: module.name,
          },
        };
      },
      providesTags: ['Categories'],
      keepUnusedDataFor: 0,
    }),
    fetchLinkedItems: builder.query({
      query: ({ projectId, ids }) => ({
        url: `projects/${projectId}/form-instances/linked-items`,
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
      transformResponse: response =>
        response.values.map(value => ({
          ...value,
          isDeleted: value?.is_deleted,
          moduleId: value?.module_id,
          statusColor: value?.color,
          num: value?.legacy_id,
          templateName: value.template_name,
        })),
      providesTags: ['LinkedForms'],
    }),
    fetchForm: builder.query({
      query: ({ projectId, formId }) => ({
        url: `projects/${projectId}/form-instances/${formId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    unlinkItems: builder.mutation({
      query: ({ ids, projectId, formAnswerId, type }) => ({
        url: `projects/${projectId}/form-answers/${formAnswerId}/unlink-items`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: ids, type }),
      }),
    }),
    deleteAnswer: builder.mutation({
      query: ({ projectId, formAnswerId }) => ({
        url: `projects/${projectId}/form-answers/${formAnswerId}`,
        method: 'DELETE',
      }),
    }),
    fetchChoices: builder.query({
      query: ({ projectId, fieldId, offset = 0, limit = 1000 }) => ({
        url: `projects/${projectId}/form-fields/${fieldId}/choices`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: {
          offset,
          limit,
        },
      }),
    }),
    fetchSections: builder.query({
      query: ({ projectId, formInstanceId, sectionId, offset = 0, limit = 1000 }) => ({
        url: `projects/${projectId}/form-instances/${formInstanceId}/sections/${sectionId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: {
          offset,
          limit,
        },
      }),
    }),
    fetchFormSections: builder.query({
      query: ({ projectId, formInstanceId }) => ({
        url: `projects/${projectId}/form-instances/${formInstanceId}/sections`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: response => ({
        formTemplateInstanceId: response?.form_template_instance_id,
        sections: (response?.form_template_sections || []).map(section => ({
          ...section,
          form_template_instance_id: response?.form_template_instance_id,
        })),
      }),
      providesTags: ['FormSections'],
    }),
    saveFormAnswersWithMediaBulk: builder.mutation({
      query: ({ projectId, formInstanceId, fieldId, formAnswers }) => ({
        url: `projects/${projectId}/form-answers-with-media-bulk`,
        method: 'POST',
        headers: { 'Cache-Control': 'No-Store', 'Content-Type': 'application/json' },
        body: {
          form_instance_id: formInstanceId,
          field_id: fieldId,
          create_form_template_instance: true,
          client_created_at: new Date().toISOString(),
          client_updated_at: new Date().toISOString(),
          form_answers: formAnswers,
        },
      }),
    }),
    setFormInstanceDynamicProperty: builder.mutation({
      query: ({ projectId, formInstanceId, dynamicPropertyId, value }) => ({
        url: `projects/${projectId}/form-instances/${formInstanceId}/set-dynamic-property`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: {
          dynamic_property_id: dynamicPropertyId,
          value,
        },
      }),
    }),
    setFormInstanceStatus: builder.mutation({
      query: ({ projectId, formInstanceId, statusId }) => ({
        url: `projects/${projectId}/form-instances/${formInstanceId}/set-status`,
        method: 'PUT',
        headers: { 'Cache-Control': 'No-Store', 'Content-Type': 'application/json' },
        body: {
          status_id: statusId,
        },
      }),
    }),
    importForms: builder.mutation({
      query: ({ projectId, moduleId, mediaId }) => ({
        url: `projects/${projectId}/import/forms/module/${moduleId}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          media_id: mediaId,
        },
      }),
    }),
    downloadFormsImportTemplate: builder.mutation({
      async queryFn({ projectId, moduleId }, _queryApi, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `projects/${projectId}/import/template/forms/module/${moduleId}`,
          method: 'GET',
          responseHandler: response => response.blob(),
        });

        if (result.error) return { error: result.error };
        const blob = result.data;
        const headers = result?.meta?.response?.headers;
        const filename = headers?.get('content-disposition')
          ? getNameFromContentDisposition(headers)
          : 'template.xlsx';
        return { data: { blob, filename } };
      },
    }),
    deleteFormInstanceDynamicProperty: builder.mutation({
      query: ({ projectId, formInstanceId, dynamicPropertyId }) => ({
        url: `projects/${projectId}/form-instances/${formInstanceId}/dynamic-properties/${dynamicPropertyId}`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchProjectFormsModulesFiltersQuery,
  useFetchCategoryTreeQuery,
  useUnlinkItemsMutation,
  useFetchLinkedItemsQuery,
  useFetchFormQuery,
  useDeleteAnswerMutation,
  useLazyFetchChoicesQuery,
  useFetchSectionsQuery,
  useLazyFetchSectionsQuery,
  useFetchFormSectionsQuery,
  useSaveFormAnswersWithMediaBulkMutation,
  useSetFormInstanceDynamicPropertyMutation,
  useSetFormInstanceStatusMutation,
  useImportFormsMutation,
  useDownloadFormsImportTemplateMutation,
  useDeleteFormInstanceDynamicPropertyMutation,
} = formsApi;
